import { useCallback, useState } from "react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth/AuthProvider";
import { createPairingCode } from "../../api/collab";

/**
 * "Connect a computer": mints a pairing code and shows the one-line command the
 * user runs on their own machine to join the workspace as a Computer.
 *
 * The command carries only the pairing code — a short-lived, single-use join
 * secret (10 min). No user token is embedded.
 */
export function ConnectComputerPanel({ baseUrl, accountId }: { baseUrl: string; accountId: string }) {
  const { client } = useAuth();
  const { t } = useI18n();
  const [code, setCode] = useState<string | null>(null);
  const [os, setOs] = useState<"unix" | "windows">(navigator.userAgent.includes("Windows") ? "windows" : "unix");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const generate = useCallback(async () => {
    if (!client) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createPairingCode(client, accountId);
      setCode(res.code);
      setCopied(false);
    } catch {
      setError(t("collab.connect.error"));
    } finally {
      setBusy(false);
    }
  }, [accountId, client, t]);

  const command =
    code == null
      ? ""
      : os === "windows"
        ? `$env:SJ_SERVER='${baseUrl}'; irm ${baseUrl}/sj/install.ps1 | iex; sj connect ${code}`
        : `curl -fsSL ${baseUrl}/sj/install.sh | SJ_SERVER=${baseUrl} sh -s -- connect ${code}`;

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(command).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [command]);

  return (
    <section className="ws-connect">
      <h3 className="ws-connect-title">{t("collab.connect")}</h3>
      <p className="ws-connect-desc">{t("collab.connect.desc")}</p>

      {code == null ? (
        <button type="button" className="ws-btn" disabled={busy} onClick={generate}>
          {busy ? "…" : t("collab.connect.generate")}
        </button>
      ) : (
        <div className="ws-connect-cmd">
          <div className="ws-os-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={os === "unix"} className={os === "unix" ? "active" : ""} onClick={() => setOs("unix")}>
              {t("collab.connect.macos")}
            </button>
            <button type="button" role="tab" aria-selected={os === "windows"} className={os === "windows" ? "active" : ""} onClick={() => setOs("windows")}>
              {t("collab.connect.windows")}
            </button>
          </div>
          <pre className="ws-cmd"><code>{command}</code></pre>
          <div className="ws-connect-actions">
            <button type="button" className="ws-btn" onClick={copy}>
              {copied ? t("collab.connect.copied") : t("collab.connect.copy")}
            </button>
            <button type="button" className="ws-btn ghost" onClick={generate} disabled={busy}>
              {t("collab.connect.generate")}
            </button>
          </div>
          <p className="ws-warn">{t("collab.connect.tokenWarning")}</p>
          <p className="ws-hint">{t("collab.connect.waiting")}</p>
        </div>
      )}
      {error ? <p className="ws-warn">{error}</p> : null}
    </section>
  );
}
